import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import Header from './components/Header';
import Hero from './components/Hero';
import ProblemSolution from './components/ProblemSolution';
import HeroHome from './components/HeroHome';
import ProblemSolutionHome from './components/ProblemSolutionHome';
import AudienceToggle from './components/AudienceToggle';
import ProductShowcase from './components/ProductShowcase';
import Process from './components/Process';
import FAQ from './components/FAQ';
import FAQHome from './components/FAQHome';
import Footer from './components/Footer';
import ROICalculatorPricing from './components/ROICalculatorPricing';
import CustomQuoteSection from './components/CustomQuoteSection';
import HomePricing from './components/HomePricing';
import AuthPages from './components/AuthPages';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Dashboard from './pages/Dashboard';
import AuthComplete from './pages/AuthComplete';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PageFR from './pages/PageFR';
import PageFRPartenaires from './pages/PageFRPartenaires';
import CustomQuote from './pages/CustomQuote';
import OnboardingPlan from './pages/OnboardingPlan';
import { getCachedUser, setToken } from './lib/api';

const DocumentTitle: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const path = (location.pathname || '').toLowerCase();
    if (path.startsWith('/fr')) {
      document.title = '3DtoVideo | Conversion Matterport en vidéo';
    } else if (path.startsWith('/partners')) {
      document.title = '3DtoVideo | Partners';
    } else {
      document.title = '3DtoVideo | Matterport to Video Service';
    }
  }, [location.pathname]);

  return null;
};

/** /Partners/… ou /FR/… ne matchent pas les routes ; on normalise vers la casse canonique. */
const CanonicalPathRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const p = location.pathname;
    const canonical = p.toLowerCase();
    if (p === canonical) return;
    if (!canonical.startsWith('/partners') && !canonical.startsWith('/fr')) return;
    navigate(`${canonical}${location.search}${location.hash}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

/** Partenaire : ne pas rester sur /dashboard ou /fr/dashboard (grille tarifs « home » / agents). */
const PartnerDashboardCanonicalRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathNorm = (location.pathname || '').toLowerCase().replace(/\/$/, '') || '/';
    if (getCachedUser()?.user_type !== 'partner') return;
    if (pathNorm === '/dashboard') {
      navigate(`/partners/dashboard${location.search}${location.hash}`, { replace: true });
    } else if (pathNorm === '/fr/dashboard') {
      navigate(`/fr/partenaires/dashboard${location.search}${location.hash}`, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

/** Google OAuth (desktop) redirige vers n'importe quelle page avec #token=...
 *  On extrait le token, on le stocke dans localStorage, puis on nettoie le hash et on recharge. */
const HashTokenExtractor: React.FC = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash || '';
    const m = hash.match(/token=([^&]+)/);
    if (m && m[1]) {
      const token = decodeURIComponent(m[1].replace(/\+/g, ' '));
      setToken(token);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      window.location.reload();
    }
  }, []);
  return null;
};

const App: React.FC = () => {
  return (
    <ToastProvider>
      <BrowserRouter>
        <HashTokenExtractor />
        <CanonicalPathRedirect />
        <PartnerDashboardCanonicalRedirect />
        <DocumentTitle />
        <Routes>
          <Route path="/fr" element={<PageFR />} />
          <Route path="/fr/partenaires" element={<PageFRPartenaires />} />
          <Route path="/photographers" element={<Navigate to="/partners" replace />} />
          <Route path="/partners" element={
            <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10">
              <Header />
              <Hero />
              <ProblemSolution />
              <Process />
              <ProductShowcase />
              <ROICalculatorPricing partnerMode signupPath="/partners/signup?section=try-one" />
              <FAQ />
              <Footer />
            </div>
          } />
          <Route path="/partners/dashboard" element={<Dashboard />} />
          {/* Auth photographes (US) — même composant que /signup / /signin avec pathname /partners → compte partner */}
          <Route path="/partners/signup" element={<AuthPages />} />
          <Route path="/partners/signin" element={<AuthPages />} />
          <Route path="/partners/onboarding/plan" element={<OnboardingPlan />} />
          <Route path="/" element={
            <div className="min-h-screen bg-gradient-to-b from-white to-[#3C59B2]/10">
              <Header />
              <HeroHome />
              <AudienceToggle />
              <Process />
              <ProductShowcase />
              <HomePricing />
              <FAQHome />
              <Footer />
            </div>
          } />
          <Route path="/custom-quote" element={<CustomQuote />} />
          <Route path="/signup" element={<AuthPages />} />
          <Route path="/signin" element={<AuthPages />} />
          <Route path="/fr/signin" element={<AuthPages />} />
          <Route path="/fr/signup" element={<AuthPages />} />
          <Route path="/fr/partenaires/signin" element={<AuthPages />} />
          <Route path="/fr/partenaires/signup" element={<AuthPages />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/fr/forgot-password" element={<ForgotPassword />} />
          <Route path="/fr/partenaires/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/fr/reset-password" element={<ResetPassword />} />
          <Route path="/auth/complete" element={<AuthComplete />} />
          <Route path="/auth/complete/:t" element={<AuthComplete />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/onboarding/plan" element={<OnboardingPlan />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/fr/dashboard" element={<Dashboard />} />
          <Route path="/fr/onboarding/plan" element={<OnboardingPlan />} />
          <Route path="/fr/partenaires/onboarding/plan" element={<OnboardingPlan />} />
          <Route path="/fr/partenaires/dashboard" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
};

export default App;